import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from './Icon'
import { languageFromPath, pathInLanguage } from '../i18n/i18n'

// "Does your phone speak eSIM?" modal — last-mile confidence check on
// the destination detail page. The earlier version pretended to be a
// search-the-database checker but really wrapped a tiny regex matcher
// and a settings tip. The new design ditches the pretense and shows
// the customer how to verify it themselves in 60 seconds: two
// side-by-side platform paths (iPhone / Android) with the exact
// settings breadcrumb, plus deeper-info links to /compatible-devices
// and /how-it-works for anyone who wants more confidence.
//
// Closes on backdrop click, Escape, or the X button. No focus trap
// because there's no form to fill — only links to follow.

interface Props {
  open: boolean
  onClose: () => void
}

export function CompatibilityModal({ open, onClose }: Props) {
  const { t } = useTranslation('common')
  const location = useLocation()
  const lang = languageFromPath(location.pathname)
  const withLang = (p: string) => pathInLanguage(p, lang)

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  // One platform card (iPhone / Android). `base` is the i18n key prefix so
  // the same card renders for both checks. `steps` is the list of numbered
  // action steps; `verdictKey` is the i18n key for the green footer — the
  // "what success looks like" line. For the eSIM check that's a dedicated
  // `verdict` string; for the unlock check it's `step3` (the target state),
  // which lets both checks share the same header → steps → verdict shape
  // and keeps the two cards in a row aligned to an equal height.
  const renderPath = (
    base: string,
    key: 'ios' | 'android',
    icon: 'apple' | 'android',
    steps: number[],
    verdictKey: string,
  ) => (
    <div key={key} className="compat-path">
      <div className="compat-path__head">
        <span className="compat-path__icon"><Icon name={icon} size={17} /></span>
        <span className="compat-path__label">{t(`${base}.${key}.label`)}</span>
      </div>
      <ol className="compat-path__steps">
        {steps.map((n) => (
          <li key={n} className="compat-path__step">
            <span className="compat-path__num">{n}</span>
            <span>{t(`${base}.${key}.step${n}`)}</span>
          </li>
        ))}
      </ol>
      <div className="compat-path__verdict">
        <Icon name="check" size={12} />
        {t(`${base}.${key}.${verdictKey}`)}
      </div>
    </div>
  )

  return (
    <div className="compat-modal" role="dialog" aria-modal="true" aria-label={t('compatModal.ariaLabel')}>
      <div className="compat-modal__backdrop" onClick={onClose} />
      <div className="compat-modal__panel">
        <button className="compat-modal__close" onClick={onClose} aria-label={t('compatModal.close')}>
          <Icon name="x" size={14} />
        </button>

        <div className="compat-modal__eyebrow">{t('compatModal.eyebrow')}</div>
        <h2 className="compat-modal__title">{t('compatModal.title')}</h2>
        <p className="compat-modal__sub">{t('compatModal.sub')}</p>

        {/* Check 1 — eSIM-compatible (kept as-is: two paths + reassurance) */}
        <section className="compat-modal__check">
          <div className="compat-modal__check-head">
            <span className="compat-modal__check-num">1</span>
            <span className="compat-modal__check-title">{t('compatModal.check1.title')}</span>
          </div>
          <div className="compat-modal__paths">
            {renderPath('compatModal', 'ios', 'apple', [1, 2, 3], 'verdict')}
            {renderPath('compatModal', 'android', 'android', [1, 2, 3], 'verdict')}
          </div>
          <div className="compat-modal__reassure">
            <span className="compat-modal__dot" aria-hidden />
            {t('compatModal.reassure')}
          </div>
          <Link to={withLang('/compatible-devices')} className="compat-modal__link compat-modal__check-link" onClick={onClose}>
            {t('compatModal.browseAll')} <Icon name="arrow" size={12} />
          </Link>
        </section>

        {/* Check 2 — network-unlocked (steps copied from the how-it-works guide) */}
        <section className="compat-modal__check">
          <div className="compat-modal__check-head">
            <span className="compat-modal__check-num">2</span>
            <span className="compat-modal__check-title">{t('compatModal.check2.title')}</span>
          </div>
          <p className="compat-modal__check-desc">{t('compatModal.check2.desc')}</p>
          <div className="compat-modal__paths">
            {renderPath('compatModal.unlock', 'ios', 'apple', [1, 2], 'step3')}
            {renderPath('compatModal.unlock', 'android', 'android', [1, 2], 'step3')}
          </div>
        </section>

        <div className="compat-modal__links">
          <Link to={withLang('/how-it-works')} className="compat-modal__link" onClick={onClose}>
            {t('compatModal.fullGuide')} <Icon name="arrow" size={12} />
          </Link>
        </div>
      </div>
    </div>
  )
}
